import {
    Box,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputAdornment,
    TextField,
    TextFieldProps,
} from "@mui/material";
import * as React from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { AccExecClientResponse } from "../../../../services/types/clientTypes";
import FlexGrid from "../../../../layouts/flexGrid";
import AccountManagerSubDataGridPro from "./accountManagerSubDataGridPro";
import CclDrawer from "../../../../components/common/cclDrawer/cclDrawer";
import CclButton from "../../../../components/common/cclButtons/cclButton";

export type EditAccountManagerDialogProps = {
    open: boolean;
    onOk: (mapperId:number, fiscalYear: number, newClientIndicator: number, effectiveStartDate: Date | null | undefined, effectiveEndDate: Date | null | undefined) => void;
    onCancel: () => any;
    AccountCEResponseSelect: AccExecClientResponse[];
    dialogMode: "select" | "edit";
};

const parseServerDate = (value: Date | string | null | undefined): Date | null => {
    if (!value) return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    // The API can return malformed separators around the time portion (e.g. a colon
    // instead of a period before fractional seconds), which native Date parsing rejects
    // outright. Extract the date/time components directly and ignore anything after
    // seconds (fraction, timezone, etc.) rather than relying on the string being valid ISO.
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
    if (match) {
        const [, y, mo, d, h, mi, s] = match;
        const parsed = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const EditAccountManagerDialog: React.FC<EditAccountManagerDialogProps> = (props) => {
    const [title, setTitle] = React.useState<string>("");
    const [fiscalYear, setFiscalYear] = React.useState<number>(0);
    const [newClientIndicator, setNewClientIndicator] = React.useState<number>(0);
    const [effectiveStartDate, setEffectiveStartDate] = React.useState<Date|null>();
    const [effectiveEndDate, setEffectiveEndDate] = React.useState<Date|null>();
    const [mapperID, setMapperId] = React.useState<number>(0);
    const [dateValidationMessage, setDateValidationMessage] = React.useState<string>("");

    React.useEffect(() => {   
        if(props.AccountCEResponseSelect.length >0 && props.open)
        {
            setFiscalYear(props.AccountCEResponseSelect[0].fiscalYear);
            setNewClientIndicator(props.AccountCEResponseSelect[0].newClientIndicator);
            setEffectiveStartDate(parseServerDate(props.AccountCEResponseSelect[0].effectiveStartDate));
            setEffectiveEndDate(parseServerDate(props.AccountCEResponseSelect[0].effectiveEndDate));
            setTitle(props.AccountCEResponseSelect[0].parentKey.toString() + "-" + props.AccountCEResponseSelect[0].parentName);
            setMapperId(props.AccountCEResponseSelect[0].mapperID)
        }
    }, [props.AccountCEResponseSelect, props.open]);

    const fiscalYearMinDate = React.useMemo(() => new Date(fiscalYear - 1, 3, 1), [fiscalYear]);
    const fiscalYearMaxDate = React.useMemo(() => new Date(fiscalYear, 2, 31), [fiscalYear]);

    const stripTime = (date: Date) =>
        new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

    const validateForm = (): boolean => {
        setDateValidationMessage("");
        if (!effectiveStartDate || !effectiveEndDate) {
            setDateValidationMessage("Date should not be empty");
            return false;
        }
        const start = stripTime(effectiveStartDate);
        const end = stripTime(effectiveEndDate);
        const minFy = stripTime(fiscalYearMinDate);
        const maxFy = stripTime(fiscalYearMaxDate);

        if (start === end) {
            setDateValidationMessage("Date should not be same");
            return false;
        }
        if (start > end) {
            setDateValidationMessage("Please Select Proper Date");
            return false;
        }
        if (start < minFy || start > maxFy || end < minFy || end > maxFy) {
            setDateValidationMessage("Effective dates must be within the selected Fiscal Year");
            return false;
        }
        return true;
    };

    const handleOk = () => {
        if (!validateForm()) return;
        props.onOk(mapperID,fiscalYear, newClientIndicator, effectiveStartDate, effectiveEndDate);
    };

    const handleKeyPress = async (evt: React.KeyboardEvent<HTMLDivElement>) => {
        if (evt.key === "Enter") {
            handleOk();
        }
    };

    React.useEffect(() => {
        validateForm();
        // eslint-disable-next-line
    }, [effectiveStartDate, effectiveEndDate, fiscalYearMinDate, fiscalYearMaxDate]);

    return (
        <CclDrawer 
            title={title} 
            open={props.open} 
            onClose={()=> props.onCancel()}
        >
                <TextField
                    name="FiscalYearValue"
                    placeholder="Fiscal Year"
                    value={fiscalYear}
                    id="outlined-adornment-new-user-first-name"
                    label="Fiscal Year"
                    variant="outlined"
                    fullWidth={true}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                {/* <AccountBoxIcon /> */}
                            </InputAdornment>
                        ),
                    }}
                    onChange={(e) => {
                        e.preventDefault();
                        setFiscalYear(parseInt(e.target.value));
                    }}
                    onKeyPress={(e) => handleKeyPress(e)}
                    sx={{ mt: 1, mb: 1 }}
                />
                <FormControlLabel
                    label="Secondary Mapping"
                    sx={{ mt: 1, mb: 1 }}
                    control={
                        <Checkbox
                            name="newClientIndicator"
                            checked={Boolean(newClientIndicator)}
                            onChange={(e) => {
                                setNewClientIndicator(e.target.checked ? 1 : 0);
                            }}
                        />
                    }
                />

                <DatePicker
                label="Effective Start Date"
                inputFormat="MM/dd/yyyy"
                minDate={fiscalYearMinDate}
                maxDate={fiscalYearMaxDate}
                value= {effectiveStartDate}
                onChange={(newValue: Date | null) => setEffectiveStartDate(newValue)}
                                                   renderInput={(params) => (
                                                       <TextField {...params} fullWidth />
                                                   )}
                ></DatePicker>
                <Box sx={{m: 3}} />
                <DatePicker
                label="Effective End Date"
                inputFormat="MM/dd/yyyy"
                minDate={fiscalYearMinDate}
                maxDate={fiscalYearMaxDate}
                value={effectiveEndDate}
                onChange={(newValue: Date | null) => setEffectiveEndDate(newValue)}
                                                    renderInput={(params) => (
                                                        <TextField {...params} fullWidth />
                                                    )}
                ></DatePicker>
                <Box sx={{m: 3}} />
                <FlexGrid>
                    <AccountManagerSubDataGridPro
                        accountCELists={props.AccountCEResponseSelect ?? []}
                        isLoading={false} 
                    />
                </FlexGrid>
                <Box>
                {dateValidationMessage && (
                    <FormHelperText error>{dateValidationMessage}</FormHelperText>
                )}</Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-end",
                        mt: 1,
                    }}
                >
                    <CclButton
                        id="cancel-btn"
                        onClick={() => 
                            props.onCancel()
                        }
                        sx={{
                            mr: 1
                        }} 
                        mode={"primary"}                
                    >
                        Cancel
                    </CclButton>
                    <CclButton
                        id="ok-btn"
                        onClick={handleOk}
                        disabled={!!dateValidationMessage}
                        mode={"primary"}
                       
                    >
                        {props.dialogMode === "select" ? "Add" : "Save"}
                    </CclButton>
                </Box>
        </CclDrawer>
    );
};

export default EditAccountManagerDialog;
